import React from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Card,
  Typography,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  TableRow,
  Paper,
  Button,
  Tooltip,
  Chip,
  Stack,
  Avatar,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import { 
  QrCodeScanner, 
  Email, 
  WhatsApp, 
  Sms, 
  Person, 
  Phone, 
  Description, 
  PermContactCalendar, 
  CalendarToday, 
  CalendarMonth, 
  Subject, 
  PhotoCamera, 
  Article,
  Close as CloseIcon,
  HowToReg,
  ExitToApp,
  Refresh,
  LogoutOutlined,
  CheckCircleOutlined,
  EventBusy,
  CheckCircle,
  Cancel,
  Info as InfoIcon,
  HourglassEmpty as HourglassEmptyIcon
} from "@mui/icons-material";
import CBackDrop from "../../../basecomp/components/CBackDrop";

import VisitorGatepassVM from "../viewmodal/VisitorGatepassVM";

const VisitorGatepassView = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const {
    id,
    visitorData,
    loading,
    disabled,
    openPhotoPreview,
    openDocumentPreview,
    photoPreview,
    documentPreview,
    canCheckIn,
    canCheckOut,
    scannerOpen,
    showConfirmation,
    confirmationType,
    isCheckedIn,
    notificationStatus,
    handleCheckIn,
    handleCheckOut,
    handleResubmit,
    handleOpenPhotoPreview,
    handleClosePhotoPreview,
    handleOpenDocumentPreview,
    handleCloseDocumentPreview,
    handleBackFromConfirmation,
    handleOpenScanner,
    handleCloseScanner,
    handleQrCodeScanned,
    formatDate,
  } = VisitorGatepassVM();

  if (loading) return <CBackDrop cOpen={loading} />;

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "Approved":
        return { color: "#1b5e20", bgColor: "#e8f5e9", icon: <CheckCircle fontSize="small" /> };
      case "Rejected":
        return { color: "#b71c1c", bgColor: "#ffebee", icon: <Cancel fontSize="small" /> };
      case "On Hold":
        return { color: "#e65100", bgColor: "#fff3e0", icon: <HourglassEmptyIcon fontSize="small" /> };
      default:
        return { color: "#0d47a1", bgColor: "#e3f2fd", icon: <InfoIcon fontSize="small" /> };
    }
  };

  // Status chip component
  const StatusChip = ({ status }) => {
    const { color, bgColor, icon } = getStatusColor(status || "Unknown");
    
    return (
      <Chip
        icon={icon}
        label={status || "Unknown"}
        sx={{ 
          fontWeight: 600,
          color: color,
          bgcolor: bgColor,
          "& .MuiChip-icon": {
            color: color
          }
        }}
      />
    );
  };

  // Confirmation Screen
  if (showConfirmation) {
    return (
      <Box 
        sx={{ 
          backgroundColor: "#f5f7fa",
          minHeight: "100vh",
          py: { xs: 2, sm: 4, md: 6 },
          px: { xs: 2, sm: 3, md: 4 },
          mt: { xs: 10, sm: 4, md: 5 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Container maxWidth="sm">
          <Card
            elevation={3}
            sx={{
              borderRadius: "16px",
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                backgroundImage: "linear-gradient(120deg, #0d47a1, #0d47a1)",
                color: "white",
                padding: { xs: "1.5em", sm: "2em" },
                position: "relative",
              }}
            >
              <IconButton
                aria-label="go back"
                onClick={handleBackFromConfirmation}
                sx={{
                  position: "absolute",
                  top: 8,
                  left: 8,
                  backgroundColor: "rgba(255,255,255,0.2)",
                  color: "white",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.3)" },
                  zIndex: 10,
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              
              {/* Content container with more spacing from the top */}
              <Box sx={{ 
                textAlign: "center", 
                pt: { xs: 6, sm: 0 }, // Add significant padding-top on mobile (48px)
                mt: { xs: 2, sm: 0 }  // Extra margin on mobile for safety
              }}>
                <Typography
                  variant={isMobile ? "h5" : "h4"}
                  sx={{ 
                    fontWeight: "bold", 
                    textShadow: "0px 2px 4px rgba(0,0,0,0.3)",
                  }}
                >
                  {confirmationType === "resubmit"
                    ? "Request Resubmitted"
                    : confirmationType === "checkin"
                    ? "Check-In Successful"
                    : "Check-Out Successful"}
                </Typography>
                
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    mt: 1, 
                    opacity: 0.9,
                    fontWeight: 400,
                  }}
                >
                  {confirmationType === "resubmit" 
                    ? "The visitor request is now pending approval"
                    : confirmationType === "checkin"
                    ? "Visitor has been successfully checked in"
                    : "Visitor has completed their visit"}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ p: 3 }}>
              {/* Notification Status Section */}
              {notificationStatus && (
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 2, textAlign: "center" }}
                  >
                    Notifications Sent:
                  </Typography>
                  
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: "#f8f9fa",
                      border: "1px solid rgba(0,0,0,0.09)",
                      mb: 3
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="center"
                      flexWrap="wrap"
                    >
                      {notificationStatus.email && (
                        <Chip
                          icon={<Email />}
                          label="Email"
                          color="success"
                          variant="outlined"
                          sx={{ m: 0.5 }}
                        />
                      )}

                      {notificationStatus.sms && (
                        <Chip
                          icon={<Sms />}
                          label="SMS"
                          color="success"
                          variant="outlined"
                          sx={{ m: 0.5 }}
                        />
                      )}

                      {(notificationStatus.whatsapp ||
                        notificationStatus.whatsapp_approval ||
                        notificationStatus.whatsapp_resubmit) && (
                        <Chip
                          icon={<WhatsApp />}
                          label="WhatsApp"
                          color="success"
                          variant="outlined"
                          sx={{ m: 0.5 }}
                        />
                      )}

                      {!notificationStatus.email &&
                        !notificationStatus.sms &&
                        !notificationStatus.whatsapp &&
                        !notificationStatus.whatsapp_approval &&
                        !notificationStatus.whatsapp_resubmit && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            align="center"
                          >
                            No notifications were sent successfully.
                          </Typography>
                        )}
                    </Stack>
                  </Paper>
                </Box>
              )}
              
              <Button
                variant="contained"
                fullWidth
                onClick={handleBackFromConfirmation}
                sx={{ 
                  mt: 2, 
                  py: 1.2,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600
                }}
              >
                Return to Gatepass
              </Button>
            </Box>
          </Card>
        </Container>
      </Box>
    );
  }

  // Scanner prompt when no ID
  if (!id) {
    return (
      <Box 
        sx={{ 
          backgroundColor: "#f5f7fa",
          minHeight: "100vh",
          py: { xs: 2, sm: 4, md: 6 },
          px: { xs: 2, sm: 3, md: 4 },
          mt: { xs: 10, sm: 4, md: 5 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Container maxWidth="sm">
          <Card
            elevation={3}
            sx={{
              padding: "2em",
              borderRadius: "16px",
              backgroundColor: "#fff",
              textAlign: "center",
            }}
          >
            <Typography
              variant="h5"
              sx={{ 
                fontWeight: "bold", 
                color: "#1976d2",
                mb: 3
              }}
            >
              VISITOR GATEPASS
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 4,
                backgroundColor: "#f8fafd",
                borderRadius: 2,
                border: "1px dashed #1976d2",
                mb: 3,
              }}
            >
              <QrCodeScanner
                sx={{
                  fontSize: 80,
                  color: "#1976d2",
                  mb: 2,
                  cursor: "pointer",
                }}
                onClick={handleOpenScanner}
              />
              <Typography 
                variant="body1" 
                color="text.secondary"
                align="center" 
                sx={{ 
                  mb: 2,
                  mx: 2
                }}
              >
                Scan a visitor's QR code to check them in or out
              </Typography>
              <Button
                variant="contained"
                startIcon={<QrCodeScanner />}
                onClick={handleOpenScanner}
                size="large"
                sx={{ 
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3
                }}
              >
                Scan QR Code
              </Button>
            </Box>
          </Card>
        </Container>
      </Box>
    );
  }

  // Empty Data State
  if (!visitorData || Object.keys(visitorData).length === 0) {
    return (
      <Box 
        sx={{ 
          backgroundColor: "#f5f7fa",
          minHeight: "100vh",
          py: { xs: 2, sm: 4, md: 6 },
          px: { xs: 2, sm: 3, md: 4 },
          mt: { xs: 10, sm: 4, md: 5 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Container maxWidth="sm">
          <Card
            elevation={3}
            sx={{
              padding: "2em",
              borderRadius: "16px",
              backgroundColor: "#fff",
              textAlign: "center",
            }}
          >
            <Avatar 
              sx={{ 
                width: 60, 
                height: 60, 
                bgcolor: "#f44336", 
                margin: "0 auto 1em",
              }}
            >
              <Cancel fontSize="large" />
            </Avatar>
            <Typography 
              variant="h5" 
              gutterBottom 
              fontWeight="600"
            >
              Data Not Found
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ mb: 3 }}
            >
              Visitor data could not be loaded or doesn't exist
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => window.history.back()} 
              startIcon={<ArrowBackIcon />}
              size="large"
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                py: 1,
              }}
            >
              Go Back
            </Button>
          </Card>
        </Container>
      </Box>
    );
  }

  // Main Visitor Gatepass View
  return (
    <Box 
      sx={{ 
        backgroundColor: "#f5f7fa",
        minHeight: "100vh",
        py: { xs: 2, sm: 4, md: 6 },
        px: { xs: 2, sm: 3, md: 4 },
        mt: { xs: 10, sm: 4, md: 5 }, // Match margin top from VisitorApprovalView
      }}
    >
      <Container maxWidth="md">
        <Card
          elevation={3}
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
          }}
        >
          {/* Header with gradient background */}
          <Box
            sx={{
              backgroundImage: "linear-gradient(120deg, #0d47a1, #0d47a1)",
              color: "white",
              padding: { xs: "1.5em", sm: "2em" },
              position: "relative",
            }}
          >
            <IconButton
              aria-label="go back"
              onClick={() => window.history.back()}
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                backgroundColor: "rgba(255,255,255,0.2)",
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.3)",
                },
                zIndex: 10,
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            
            <Box sx={{ 
              textAlign: "center", 
              pt: { xs: 6, sm: 0 }, 
              mt: { xs: 2, sm: 0 } 
            }}>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                sx={{ 
                  fontWeight: "bold", 
                  letterSpacing: 1,
                  textShadow: "0px 2px 4px rgba(0,0,0,0.3)",
                }}
              >
                VISITOR GATEPASS
              </Typography>
              
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  mt: 1, 
                  opacity: 0.9,
                  fontWeight: 400,
                  letterSpacing: 0.5 
                }}
              >
                Visitor check-in and check-out management
              </Typography>
            </Box>
          </Box>

          {/* Status Section */}
          <Box 
            sx={{ 
              p: { xs: "1.2em", sm: "1.5em 2em" }, 
              backgroundColor: "#f8fafd",
              borderBottom: "1px solid #eaeff4",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="body1"
              sx={{ 
                color: "#546e7a", 
                fontWeight: "500", 
                marginBottom: "0.5em",
                fontSize: { xs: "0.9rem", sm: "1rem" },
              }}
            >
              Visitor Status
            </Typography>
            
            <StatusChip status={visitorData.status} />
            
            {visitorData.check_in && !visitorData.check_out && (
              <Typography
                variant="body2"
                sx={{ 
                  marginTop: "0.8em", 
                  color: "#78909c",
                  fontStyle: "italic",
                }}
              >
                Checked in at {new Date(visitorData.check_in).toLocaleString()}
              </Typography>
            )}

            {visitorData.check_out && (
              <Typography
                variant="body2"
                sx={{ 
                  marginTop: "0.8em", 
                  color: "#78909c",
                  fontStyle: "italic",
                }}
              >
                Checked out at {new Date(visitorData.check_out).toLocaleString()}
              </Typography>
            )}
          </Box>

          {/* Visitor Info Content */}
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Mobile View */}
            {isMobile ? (
              <List sx={{ mb: 3 }}>
                <ListItem>
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: "primary.light" }}>
                      <Person />
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText 
                    primary="Visitor Name" 
                    secondary={visitorData.full_name || "Not Available"}
                    primaryTypographyProps={{ 
                      variant: "body2", 
                      color: "text.secondary",
                      fontWeight: 600,
                    }}
                    secondaryTypographyProps={{ 
                      variant: "body1",
                      fontWeight: 500,
                    }}
                  />
                </ListItem>
                <Divider variant="inset" component="li" />

                <ListItem>
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: "primary.light" }}>
                      <Phone />
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText 
                    primary="Contact Number" 
                    secondary={visitorData.contact_number || "Not Available"}
                    primaryTypographyProps={{ 
                      variant: "body2", 
                      color: "text.secondary",
                      fontWeight: 600,
                    }}
                  />
                </ListItem>
                <Divider variant="inset" component="li" />

                <ListItem>
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: "primary.light" }}>
                      <Email />
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText 
                    primary="Email" 
                    secondary={visitorData.email || "Not Available"}
                    primaryTypographyProps={{ 
                      variant: "body2", 
                      color: "text.secondary",
                      fontWeight: 600,
                    }}
                  />
                </ListItem>
                <Divider variant="inset" component="li" />

                <ListItem>
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: "primary.light" }}>
                      <CalendarToday />
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText 
                    primary="Visit From" 
                    secondary={formatDate(visitorData.visit_from) || "Not Available"}
                    primaryTypographyProps={{ 
                      variant: "body2", 
                      color: "text.secondary",
                      fontWeight: 600,
                    }}
                  />
                </ListItem>
                <Divider variant="inset" component="li" />

                <ListItem>
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: "primary.light" }}>
                      <CalendarMonth />
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText 
                    primary="Visit Till" 
                    secondary={formatDate(visitorData.visit_till) || "Not Available"}
                    primaryTypographyProps={{ 
                      variant: "body2", 
                      color: "text.secondary",
                      fontWeight: 600,
                    }}
                  />
                </ListItem>
                <Divider variant="inset" component="li" />

                <ListItem>
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: "primary.light" }}>
                      <Subject />
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText 
                    primary="Purpose" 
                    secondary={visitorData.purpose || "Not Available"}
                    primaryTypographyProps={{ 
                      variant: "body2", 
                      color: "text.secondary",
                      fontWeight: 600,
                    }}
                  />
                </ListItem>
                <Divider variant="inset" component="li" />

                <ListItem>
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: "primary.light" }}>
                      <PermContactCalendar />
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText 
                    primary="Whom to Visit" 
                    secondary={visitorData.whom_to_visit || "Not Available"}
                    primaryTypographyProps={{ 
                      variant: "body2", 
                      color: "text.secondary",
                      fontWeight: 600,
                    }}
                  />
                </ListItem>
                <Divider variant="inset" component="li" />

                {/* Photo and Document in Mobile View */}
                <Grid container spacing={2} sx={{ mt: 2, px: 2 }}>
                  {/* Photo */}
                  <Grid item xs={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: "8px",
                        border: "1px solid #eaeff4",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: "rgba(25, 118, 210, 0.1)",
                            mr: 1,
                          }}
                        >
                          <PhotoCamera fontSize="small" color="primary" />
                        </Avatar>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: "bold", color: "#546e7a" }}
                        >
                          Photo
                        </Typography>
                      </Box>
                      
                      {visitorData.photo_url ? (  // Change from visitorData.photo to visitorData.photo_url
  <Box 
    sx={{ 
      mt: 1, 
      flex: 1, 
      display: "flex", 
      justifyContent: "center",
      alignItems: "center", 
    }}
  >
    <Box
      sx={{
        width: "100%",
        paddingTop: "100%", // 1:1 Aspect ratio
        position: "relative",
        borderRadius: "8px",
        overflow: "hidden",
        border: "1px solid #e0e0e0",
        cursor: "pointer",
      }}
      onClick={handleOpenPhotoPreview}
    >
      <img
        src={visitorData.photo_url}  // Change from `data:image/jpeg;base64,${visitorData.photo}`
        alt="Visitor Photo"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = "";
        }}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
    </Box>
  </Box>
) : (
                        <Box 
                          sx={{ 
                            mt: 1, 
                            display: "flex", 
                            justifyContent: "center",
                            alignItems: "center", 
                            bgcolor: "#f5f5f5", 
                            borderRadius: "8px",
                            p: 2,
                            height: "100%",
                          }}
                        >
                          <Typography variant="body2" color="textSecondary">
                            No Photo
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                  
                  {/* Document */}
                  <Grid item xs={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: "8px",
                        border: "1px solid #eaeff4",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: "rgba(25, 118, 210, 0.1)",
                            mr: 1,
                          }}
                        >
                          <Article fontSize="small" color="primary" />
                        </Avatar>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: "bold", color: "#546e7a" }}
                        >
                          Document
                        </Typography>
                      </Box>
                      
                      {visitorData.document_url ? (  // Change from visitorData.document to visitorData.document_url
  <Box 
    sx={{ 
      mt: 1, 
      flex: 1, 
      display: "flex", 
      justifyContent: "center",
      alignItems: "center", 
    }}
  >
    <Box
      sx={{
        width: "100%",
        paddingTop: "100%", // 1:1 Aspect ratio
        position: "relative",
        borderRadius: "8px",
        overflow: "hidden",
        border: "1px solid #e0e0e0",
        cursor: "pointer",
      }}
      onClick={handleOpenDocumentPreview}
    >
      <img
        src={visitorData.document_url}  // Change from `data:image/jpeg;base64,${visitorData.document}`
        alt="Visitor Document"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = "";
        }}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
    </Box>
  </Box>
) : (
                        <Box 
                          sx={{ 
                            mt: 1, 
                            display: "flex", 
                            justifyContent: "center",
                            alignItems: "center", 
                            bgcolor: "#f5f5f5", 
                            borderRadius: "8px",
                            p: 2,
                            height: "100%",
                          }}
                        >
                          <Typography variant="body2" color="textSecondary">
                            No Document
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </List>
            ) : (
              /* Desktop Table View */
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ 
                  borderRadius: "12px", 
                  overflow: "hidden",
                  border: "1px solid #eaeff4",
                  mb: 3
                }}
              >
                <Table>
                  <TableBody>
                    {/* Visitor Information */}
                    <TableRow sx={{ "&:nth-of-type(odd)": { backgroundColor: "#fafbfd" } }}>
                      <TableCell 
                        sx={{ 
                          width: "35%", 
                          pl: 3,
                          borderBottom: "1px solid #eaeff4",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Person color="primary" />
                          <Typography
                            variant="body1"
                            sx={{ 
                              fontWeight: "600", 
                              color: "#546e7a",
                              ml: 1.5,
                            }}
                          >
                            Visitor Name
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          pl: 2,
                          borderBottom: "1px solid #eaeff4",
                        }}
                      >
                        <Typography variant="body1" sx={{ color: "#263238" }}>
                          {visitorData.full_name || "Not Available"}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    <TableRow sx={{ "&:nth-of-type(even)": { backgroundColor: "#fafbfd" } }}>
                      <TableCell 
                        sx={{ 
                          width: "35%", 
                          pl: 3,
                          borderBottom: "1px solid #eaeff4",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Phone color="primary" />
                          <Typography
                            variant="body1"
                            sx={{ 
                              fontWeight: "600", 
                              color: "#546e7a",
                              ml: 1.5,
                            }}
                          >
                            Contact Number
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          pl: 2,
                          borderBottom: "1px solid #eaeff4",
                        }}
                      >
                        <Typography variant="body1" sx={{ color: "#263238" }}>
                          {visitorData.contact_number || "Not Available"}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    <TableRow sx={{ "&:nth-of-type(odd)": { backgroundColor: "#fafbfd" } }}>
                      <TableCell 
                        sx={{ 
                          width: "35%", 
                          pl: 3,
                          borderBottom: "1px solid #eaeff4",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Email color="primary" />
                          <Typography
                            variant="body1"
                            sx={{ 
                              fontWeight: "600", 
                              color: "#546e7a",
                              ml: 1.5,
                            }}
                          >
                            Email
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          pl: 2,
                          borderBottom: "1px solid #eaeff4",
                        }}
                      >
                        <Typography variant="body1" sx={{ color: "#263238" }}>
                          {visitorData.email || "Not Available"}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    <TableRow sx={{ "&:nth-of-type(even)": { backgroundColor: "#fafbfd" } }}>
                      <TableCell 
                        sx={{ 
                          width: "35%", 
                          pl: 3,
                          borderBottom: "1px solid #eaeff4",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <CalendarToday color="primary" />
                          <Typography
                            variant="body1"
                            sx={{ 
                              fontWeight: "600", 
                              color: "#546e7a",
                              ml: 1.5,
                            }}
                          >
                            Visit From
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          pl: 2,
                          borderBottom: "1px solid #eaeff4",
                        }}
                      >
                        <Typography variant="body1" sx={{ color: "#263238" }}>
                          {formatDate(visitorData.visit_from) || "Not Available"}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    <TableRow sx={{ "&:nth-of-type(odd)": { backgroundColor: "#fafbfd" } }}>
                      <TableCell 
                        sx={{ 
                          width: "35%", 
                          pl: 3,
                          borderBottom: "1px solid #eaeff4",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <CalendarMonth color="primary" />
                          <Typography
                            variant="body1"
                            sx={{ 
                              fontWeight: "600", 
                              color: "#546e7a",
                              ml: 1.5,
                            }}
                          >
                            Visit Till
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          pl: 2,
                          borderBottom: "1px solid #eaeff4",
                        }}
                      >
                        <Typography variant="body1" sx={{ color: "#263238" }}>
                          {formatDate(visitorData.visit_till) || "Not Available"}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    <TableRow sx={{ "&:nth-of-type(even)": { backgroundColor: "#fafbfd" } }}>
                      <TableCell 
                        sx={{ 
                          width: "35%", 
                          pl: 3,
                          borderBottom: "1px solid #eaeff4",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Subject color="primary" />
                          <Typography
                            variant="body1"
                            sx={{ 
                              fontWeight: "600", 
                              color: "#546e7a",
                              ml: 1.5,
                            }}
                          >
                            Purpose
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          pl: 2,
                          borderBottom: "1px solid #eaeff4",
                        }}
                      >
                        <Typography variant="body1" sx={{ color: "#263238" }}>
                          {visitorData.purpose || "Not Available"}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    <TableRow sx={{ "&:nth-of-type(odd)": { backgroundColor: "#fafbfd" } }}>
                      <TableCell 
                        sx={{ 
                          width: "35%", 
                          pl: 3,
                          borderBottom: "1px solid #eaeff4",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <PermContactCalendar color="primary" />
                          <Typography
                            variant="body1"
                            sx={{ 
                              fontWeight: "600", 
                              color: "#546e7a",
                              ml: 1.5,
                            }}
                          >
                            Whom to Visit
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          pl: 2,
                          borderBottom: "1px solid #eaeff4",
                        }}
                      >
                        <Typography variant="body1" sx={{ color: "#263238" }}>
                          {visitorData.whom_to_visit || "Not Available"}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Check-in Status */}
                    {visitorData.check_in && (
                      <TableRow sx={{ "&:nth-of-type(even)": { backgroundColor: "#fafbfd" } }}>
                        <TableCell 
                          sx={{ 
                            width: "35%", 
                            pl: 3,
                            borderBottom: "1px solid #eaeff4",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <HowToReg color="success" />
                            <Typography
                              variant="body1"
                              sx={{ 
                                fontWeight: "600", 
                                color: "#546e7a",
                                ml: 1.5,
                              }}
                            >
                              Check-in Time
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            pl: 2,
                            borderBottom: "1px solid #eaeff4",
                          }}
                        >
                          <Chip
                            icon={<CheckCircleOutlined />}
                            label={new Date(visitorData.check_in).toLocaleString()}
                            color="success"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Check-out Status */}
                    {visitorData.check_out && (
                      <TableRow sx={{ "&:nth-of-type(odd)": { backgroundColor: "#fafbfd" } }}>
                        <TableCell 
                          sx={{ 
                            width: "35%", 
                            pl: 3,
                            borderBottom: "1px solid #eaeff4",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <LogoutOutlined color="error" />
                            <Typography
                              variant="body1"
                              sx={{ 
                                fontWeight: "600", 
                                color: "#546e7a",
                                ml: 1.5,
                              }}
                            >
                              Check-out Time
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            pl: 2,
                            borderBottom: "1px solid #eaeff4",
                          }}
                        >
                          <Chip
                            icon={<EventBusy />}
                            label={new Date(visitorData.check_out).toLocaleString()}
                            color="error"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Photo */}
                    <TableRow sx={{ "&:nth-of-type(even)": { backgroundColor: "#fafbfd" } }}>
                      <TableCell 
                        sx={{ 
                          width: "35%", 
                          pl: 3,
                          borderBottom: "1px solid #eaeff4",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <PhotoCamera color="primary" />
                          <Typography
                            variant="body1"
                            sx={{ 
                              fontWeight: "600", 
                              color: "#546e7a",
                              ml: 1.5,
                            }}
                          >
                            Photo
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          pl: 2,
                          borderBottom: "1px solid #eaeff4",
                        }}
                      >
  {visitorData.photo_url ? (  // Change from visitorData.photo
  <Tooltip title="Click to preview">
    <Box
      sx={{
        width: "120px",
        height: "120px",
        borderRadius: "8px",
        overflow: "hidden",
        border: "1px solid #e0e0e0",
        cursor: "pointer",
      }}
      onClick={handleOpenPhotoPreview}
    >
      <img
        src={visitorData.photo_url}  // Change from `data:image/jpeg;base64,${visitorData.photo}`
        alt="Visitor Photo"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = "";
        }}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
    </Box>
  </Tooltip>
) : (
                          <Typography variant="body2" color="textSecondary">
                            No Photo Available
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Document */}
                    <TableRow sx={{ "&:nth-of-type(odd)": { backgroundColor: "#fafbfd" } }}>
                      <TableCell 
                        sx={{ 
                          width: "35%", 
                          pl: 3,
                          borderBottom: "1px solid #eaeff4",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Article color="primary" />
                          <Typography
                            variant="body1"
                            sx={{ 
                              fontWeight: "600", 
                              color: "#546e7a",
                              ml: 1.5,
                            }}
                          >
                            Document
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          pl: 2,
                          borderBottom: "1px solid #eaeff4",
                        }}
                      >
     {visitorData.document_url ? (  // Change from visitorData.document
  <Tooltip title="Click to preview">
    <Box
      sx={{
        width: "120px",
        height: "120px",
        borderRadius: "8px",
        overflow: "hidden",
        border: "1px solid #e0e0e0",
        cursor: "pointer",
      }}
      onClick={handleOpenDocumentPreview}
    >
      <img
        src={visitorData.document_url}  // Change from `data:image/jpeg;base64,${visitorData.document}`
        alt="Visitor Document"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = "";
        }}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
    </Box>
  </Tooltip>
) : (
                          <Typography variant="body2" color="textSecondary">
                            No Document Available
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                mt: 3,
              }}
            >
              <Button
                variant="contained"
                color="success"
                onClick={handleCheckIn}
                disabled={!canCheckIn || disabled}
                fullWidth
                startIcon={<HowToReg />}
                sx={{ 
                  borderRadius: 2,
                  py: 1.2,
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: "0 4px 8px rgba(46, 125, 50, 0.2)",
                }}
              >
                Check In Visitor
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={handleCheckOut}
                disabled={!canCheckOut || disabled}
                fullWidth
                startIcon={<LogoutOutlined />}
                sx={{ 
                  borderRadius: 2,
                  py: 1.2,
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: "0 4px 8px rgba(211, 47, 47, 0.2)",
                }}
              >
                Check Out Visitor
              </Button>
            </Box>

            {/* Resubmit Button */}
            {!isCheckedIn && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={disabled || isCheckedIn}
                  onClick={handleResubmit}
                  fullWidth
                  startIcon={<Refresh />}
                  sx={{ 
                    borderRadius: 2,
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Resubmit Visitor Request
                </Button>
              </Box>
            )}
          </Box>
        </Card>
      </Container>

      {/* Photo Preview Dialog - Enhanced */}
      <Dialog 
        open={openPhotoPreview} 
        onClose={handleClosePhotoPreview}
        fullScreen={isMobile}
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : "12px",
            overflow: "hidden",
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#1976d2",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
          }}
        >
          <Typography variant="h6">Visitor Photo</Typography>
          <IconButton
            onClick={handleClosePhotoPreview}
            sx={{ color: "white" }}
            edge="end"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: "#000" }}>
          {photoPreview ? (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#000",
                py: 2
              }}
            >
              <img
                src={photoPreview}
                alt="Visitor Photo Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: isMobile ? "85vh" : "70vh",
                  objectFit: "contain",
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                p: 4,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "300px",
              }}
            >
              <Typography variant="body1" color="textSecondary">
                Photo not available.
              </Typography>
            </Box>
          )}
        </DialogContent>
        {!isMobile && (
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={handleClosePhotoPreview} 
              variant="contained"
              sx={{ borderRadius: "8px", textTransform: "none" }}
            >
              Close
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Document Preview Dialog - Enhanced */}
      <Dialog 
        open={openDocumentPreview} 
        onClose={handleCloseDocumentPreview}
        fullScreen={isMobile}
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : "12px",
            overflow: "hidden",
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#1976d2",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
          }}
        >
          <Typography variant="h6">Document Preview</Typography>
          <IconButton
            onClick={handleCloseDocumentPreview}
            sx={{ color: "white" }}
            edge="end"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: "#000" }}>
          {documentPreview ? (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#000",
                py: 2
              }}
            >
              <img
                src={documentPreview}
                alt="Visitor Document Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: isMobile ? "85vh" : "70vh",
                  objectFit: "contain",
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                p: 4,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "300px",
              }}
            >
              <Typography variant="body1" color="textSecondary">
                Document not available.
              </Typography>
            </Box>
          )}
        </DialogContent>
        {!isMobile && (
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={handleCloseDocumentPreview} 
              variant="contained"
              sx={{ borderRadius: "8px", textTransform: "none" }}
            >
              Close
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};

export default VisitorGatepassView;